import { ArticleWrapper, CarouselWrapper, SelectedFilter } from "./styles";
import { Block, Box } from "../../../styles/basicStyles";
import { useTranslation } from "react-i18next";
import { Carousel } from "@mantine/carousel";
import { Category } from "../../../models/blog";
import { colors } from "../../../styles/colors";
import { FC, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { AnyObject } from "../../../models/generic";
import { GenerateSlug } from "../../../utils/slug";
import SearchInput from "../../../components/inputs/searchInput";
import Typography from "../../../components/typography";

interface FiltersProps {
  categories: Category[];
  handleFilterChange: (value: string) => void;
}

let timeout: NodeJS.Timeout | null = null;

const Filters: FC<FiltersProps> = ({ categories, handleFilterChange }) => {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [inputChanged, setInputChanged] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const { t, i18n: { language } } = useTranslation();
  const router = useRouter();

  useEffect(() => {
    setInputChanged(false);
  }, []);

  useEffect(() => {
    if(router.query.category && categories.length > 0) {
      const found = categories.find(elem => GenerateSlug(elem.name[language]) === router.query.category);
      if(found) {
        setCategory(found._id);
      }
    }

    if(router.query.search && typeof router.query.search === 'string') {
      setSearchInput(router.query.search);
    }

    setIsReady(true);
  }, []);

  useEffect(() => {
    if(inputChanged && isReady) contructQuery();
  }, [category]);

  useEffect(() => {
    if(inputChanged && isReady) {
      if(timeout) clearTimeout(timeout);
      timeout = setTimeout(function() {
        contructQuery();
      }, 1000);
    }
  }, [searchInput]);

  const contructQuery = () => {
    const urlArray: string[] = [], filters: AnyObject = {};

    if(category) {
      const found = categories.find(elem => elem._id === category);
      if(found) {
        urlArray.push(`category=${GenerateSlug(found.name[language])}`);
        filters.category = found._id;
      }
    }

    if(searchInput) {
      urlArray.push(`search=${searchInput}`);
      filters.search = searchInput;
    }

    const finalUrl = `${urlArray.length > 0 ? "?" : ""}${urlArray.join("&")}`;

    router?.replace(finalUrl, undefined, { shallow: true });

    handleFilterChange(JSON.stringify(filters));
  };

  return (
    <>
      <Block
        display="flex"
        w='100%'
        fAlign={{ lg: "center", xxs: "flex-start" }}
        fDirection={{ lg: "row", xxs: "column" }}
      >
        <Box
          mt={{ lg: 0, xxs: 1 }}
          mb={{ lg: 0, xxs: 1 }}
          display={{ lg: "none", xxs: "flex" }}
        >
          <SearchInput
            placeholder="Search"
            clearable
            input={{
              value: searchInput,
              onChange: (v: string | null) => { setInputChanged(true); setSearchInput(v); }
            }}
          />
        </Box>
        <Box fAlign="flex-start" fJustify="space-between">
          {
            categories.length > 0 &&
            <Box display={{ md: "flex", xxs: "none" }} maxW={60} fWrap="wrap">
              <ArticleWrapper
                mb={1}
                mr={2.25}
                onClick={() => { setInputChanged(true); setCategory(null); }}
              >
                <SelectedFilter opacity={!category ? 1 : 0} />
                <Typography
                  fSize={{ md: 1.375, xxs: 1.063 }}
                  lHeight={1.875}
                  fWeight={!category ? 700 : 400}
                  color={!category ? colors.chineseBlack : colors.blackCow}
                >
                  {`${t('ALL').charAt(0).toUpperCase()}${t('ALL').slice(1)}`}
                </Typography>
              </ArticleWrapper>
              {
                categories.map((e, index) =>
                  <ArticleWrapper
                    mb={1}
                    key={index}
                    mr={2.25}
                    onClick={() => { setInputChanged(true); setCategory(e._id); }}
                  >
                    <SelectedFilter opacity={category === e._id ? 1 : 0} />
                    <Typography
                      fSize={{ md: 1.375, xxs: 1.063 }}
                      lHeight={1.875}
                      fWeight={category === e._id ? 700 : 400}
                      color={category === e._id ? colors.chineseBlack : colors.blackCow}
                    >
                      {e.name[language]}
                    </Typography>
                  </ArticleWrapper>
                )
              }
            </Box>
          }
          <Box
            mt={{ lg: 0, xxs: 1 }}
            mb={{ lg: 0, xxs: 1 }}
            maxW={{ md: 25, xxs: "100%" }}
            display={{ lg: "flex", xxs: "none" }}
          >
            <SearchInput
              ml={1}
              placeholder="Search"
              clearable
              input={{
                value: searchInput,
                onChange: (v: string | null) => { setInputChanged(true); setSearchInput(v); }
              }}
            />
          </Box>
        </Box>
      </Block>
      <CarouselWrapper w="100%" display={{ md: "none", xxs: "flex" }} overflow="hidden">
        {
          categories.length > 0 && 
          <Carousel 
            slideSize="auto" 
            withControls={false} 
            align="start"
            height='3rem'
          >
            <Carousel.Slide>
              <ArticleWrapper
                mr={1.813}
                ml={1.25}
                onClick={() => { setInputChanged(true); setCategory(null); }}
              >
                <SelectedFilter opacity={!category ? 1 : 0} />
                <Typography
                  fSize={{ md: 1.375, xxs: 1.063 }}
                  lHeight={1.875}
                  fWeight={!category ? 700 : 400}
                  color={!category ? colors.chineseBlack : colors.greyCloud}
                >
                  {`${t('ALL').charAt(0).toUpperCase()}${t('ALL').slice(1)}`}
                </Typography>
              </ArticleWrapper>
            </Carousel.Slide>
            {
              categories.map((e, index) =>
                <Carousel.Slide key={index}>
                  <ArticleWrapper
                    mr={1.813}
                    onClick={() => { setInputChanged(true); setCategory(e._id); }}
                  >
                    <SelectedFilter opacity={category === e._id ? 1 : 0} />
                    <Typography
                      fSize={{ md: 1.375, xxs: 1.063 }}
                      lHeight={1.875}
                      fWeight={category === e._id ? 700 : 400}
                      color={category === e._id ? colors.chineseBlack : colors.greyCloud}
                    >
                      {e.name[language]}
                    </Typography>
                  </ArticleWrapper>
                </Carousel.Slide>
              )
            }
          </Carousel>
        }
      </CarouselWrapper>
    </>
  );
};

export default Filters;
